import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { GetDependenteDto } from "../../../../shared/dtos/Dependente.dto";
import dependenteService from "../../../../services/DependenteService";
import InputComponent from "../../../../components/Input/input";
import DependenteModal from "../../../../components/DependenteModal/DependenteModal";
import Button from "../../../../components/Button/button";
import { MdOutlineViewColumn } from "react-icons/md";
import { IoIosAddCircleOutline, IoMdClose } from "react-icons/io";
import { Label } from "../../../../components/Label/styles";
import { ContainerButtons, ContainerFilter } from "../../style";
import { IoSearchSharp } from "react-icons/io5";
import { Colors } from "../../../../shared/colors/colors";
import {
  ButtonAction,
  Table,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TableContainer,
} from "../../../../components/Table/styles";
import { Dropdown } from "react-bootstrap";
import {
  MenuTable,
  MenuTableDivider,
  MenuTableItem,
} from "../../../../components/Table/styles";
import { SlOptionsVertical } from "react-icons/sl";
import {
  LoadingGif,
  LoadingOverlay,
} from "../../../../shared/styles/global.styles";
import loadSCGif from "../../../../assets/load_SC.gif";
import {
  DropdownDivider,
  DropdownScroll,
} from "../../../../components/Dropdowns/styles";
import { GetContribuinteDto } from "../../../../shared/dtos/Contribuinte.dto";
import { MainContainer } from "./styles";

interface Props {
  contribuinteData: GetContribuinteDto;
  isNewContribuinte: boolean;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
  setNewDependente: React.Dispatch<
    React.SetStateAction<GetDependenteDto | undefined>
  >;
  newFlow: boolean;
  editOnly: boolean;
}

const DependentesList: React.FC<Props> = ({
  contribuinteData,
  isNewContribuinte,
  setSelectedTab,
  setNewDependente,
  newFlow,
  editOnly,
}) => {
  const [dependentes, setDependentes] = useState<GetDependenteDto[]>(
    [] as GetDependenteDto[]
  );
  const [filteredDependentes, setFilteredDependentes] = useState<
    GetDependenteDto[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false); // Estado para o modal
  const [selectedDependente, setSelectedDependente] =
    useState<GetDependenteDto | null>(null); // Dependente selecionado
  const cpfInputRef = useRef<HTMLInputElement>(null);
  const [modalPosition, setModalPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 100, left: 200 });
  const [isOpenMenuCollumn, setIsOpenMenuCollumn] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [newDependenteData, setNewDependenteData] =
    useState<GetDependenteDto>();

  const defaultColumns = ["CPF", "Nome", "Data de Nascimento", "Celular"];

  // Função para garantir que sempre tenhamos pelo menos as colunas padrão
  const ensureDefaultColumns = (columns: string[]) => {
    return columns.length > 0 ? columns : defaultColumns;
  };

  const loadVisibleColumns = () => {
    try {
      const savedColumns = sessionStorage.getItem("dependetesVisibleColumns");
      if (!savedColumns) return defaultColumns;

      const parsedColumns = JSON.parse(savedColumns);
      return ensureDefaultColumns(parsedColumns);
    } catch (error) {
      console.error("Erro ao carregar colunas:", error);
      return defaultColumns;
    }
  };
  const [dependetesVisibleColumns, setVisibleColumns] =
    useState<string[]>(loadVisibleColumns);
  const [tempVisibleColumns, setTempVisibleColumns] = useState<string[]>([]); // Estado temporário
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = (id: number) => {
    setIsOpen(id === isOpen ? null : id);
  };

  // Atualiza a posição do seletor de colunas quando ele for aberto
  useEffect(() => {
    // Garante que o modal será aberto automaticamente se for um novo contribuinte
    if (newFlow && !showModal) {
      setShowModal(true);
    }
  }, [newFlow]);

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchValue(searchValue);

    // Limpa o timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Define o novo timeout para a busca
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        if (searchValue === "") {
          // Se o campo de busca estiver vazio, restaura a lista completa
          setFilteredDependentes(dependentes);
        } else {
          // Realiza a busca com o valor fornecido
          const query = { cpfCin: searchValue, nome: searchValue };
          const filtered = await dependenteService.searchDependente(
            query,
            contribuinteData.id
          );

          // Verifica se a resposta é válida e se há resultados
          if (filtered.dependente && filtered.dependente.length > 0) {
            setFilteredDependentes(filtered.dependente);
          } else {
            // Se não houver resultados, seta um array vazio
            setFilteredDependentes([]);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar dependentes:", error);
        setError("Não foi possível filtrar os dependentes.");
        setFilteredDependentes([]); // Se ocorrer erro, setar lista vazia
      }
    }, 500); // Espera 500ms após a última digitação
  };

  const formatCpf = (cpf: string | number): string => {
    const cpfStr = cpf.toString().padStart(11, "0");
    return cpfStr.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  // const formatDate = (date: Date | string | null): string => {
  //   if (!date) return "";
  //   const dateObj = new Date(date);
  //   return dateObj.toLocaleDateString("pt-BR");
  // };

  const handleAddDependente = () => {
    // Verifica se existe uma última movimentação e qual é o tipo
    if (
      contribuinteData.LastMovimentacao &&
      contribuinteData.LastMovimentacao.tipo === "EXCLUSAO"
    ) {
      // Caso a última movimentação seja uma exclusão, exibe um alerta
      Swal.fire(
        "<strong>Contribuinte Excluído</strong>",
        "Não é possível <strong>cadastrar ou editar</strong> um dependente quando a última movimentação do contribuinte for uma <strong>Exclusão</strong>",
        "info"
      );
    } else if (
      contribuinteData.LastMovimentacao &&
      ["REATIVACAO", "INCLUSAO"].includes(
        contribuinteData.LastMovimentacao.tipo
      )
    ) {
      // Habilita o cadastro/edição de dependentes apenas para REATIVACAO ou INCLUSAO
      setSelectedDependente(null); // Limpa o dependente selecionado para criar um novo
      setShowModal(true); // Abre o modal
    } else {
      // Caso não haja uma última movimentação válida, exibe uma mensagem genérica (opcional)
      Swal.fire(
        "Operação não permitida",
        "Não é possível adicionar ou editar dependentes neste momento.",
        "warning"
      );
    }
  };

  const handleEditDependente = (dependente: GetDependenteDto) => {
    setSelectedDependente(dependente); // Define o dependente selecionado
    setShowModal(true); // Abre o modal
  };

  const handleModalSuccess = async (dependente: any) => {
    setShowModal(false); // Fecha o modal
    // if (isNewContribuinte) {
    //   setNewDependente(dependente!);
    //   Swal.fire(
    //     "Sucesso",
    //     "Ação executada com sucesso, navegando para movimentações.",
    //     "success"
    //   );
    //   setSelectedTab(3);
    // } else {
    //   Swal.fire("Sucesso", "Ação executada com sucesso.", "success");
    //   // Atualiza a lista de dependentes
    //   const data = await dependenteService.getAllDependentesByContribuinte(
    //     contribuinteData.id
    //   );
    //   setDependentes(data);
    //   setFilteredDependentes(data); // Certifica-se de que a lista de dependentes está atualizada
    // }
    setNewDependente(dependente!);
    Swal.fire({
      title: "Sucesso",
      text: "Ação executada com sucesso, navegando para movimentações.",
      icon: "success",
      allowOutsideClick: false,
      confirmButtonText: "OK",
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedTab(3);
      }
    });
  };

  const handleDeleteDependente = async (id: number) => {
    const dependente = filteredDependentes.find((d) => d.id === id);
    if (!dependente) {
      console.error("Dependente não encontrado:", id);
      return;
    }
    const { isConfirmed } = await Swal.fire({
      title: `Tem certeza que deseja excluir o dependente ${dependente.nome}?`,
      text: "Você não poderá reverter essa ação!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
    });

    if (isConfirmed) {
      try {
        await dependenteService.deleteDependente(id);
        setDependentes((prev) => prev.filter((d) => d.id !== id));
        setFilteredDependentes((prev) => prev.filter((d) => d.id !== id));
        Swal.fire(
          "Excluído!",
          "O dependente foi removido com sucesso.",
          "success"
        );
      } catch (error) {
        console.error("Erro ao tentar excluir o dependente:", error);
        Swal.fire("Erro!", "Ocorreu um erro ao excluir o dependente.", "error");
      }
    }
  };

  useEffect(() => {
    if (isOpenMenuCollumn) {
      setTempVisibleColumns(dependetesVisibleColumns); // Inicializa o estado temporário com as colunas visíveis atuais
    }
  }, [isOpenMenuCollumn, dependetesVisibleColumns]);

  // Função para alternar colunas no estado temporário
  const handleColumnToggle = (column: string) => {
    // Se a coluna é padrão, não permite desmarcar
    if (defaultColumns.includes(column)) {
      return;
    }

    setTempVisibleColumns((prev: string[]) => {
      return prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column];
    });
  };

  // Função para aplicar as mudanças
  const handleApplyColumns = () => {
    setVisibleColumns(tempVisibleColumns); // Atualiza as colunas visíveis
    sessionStorage.setItem(
      "dependetesVisibleColumns",
      JSON.stringify(tempVisibleColumns)
    ); // Salva no sessionStorage
    setIsOpenMenuCollumn(false); // Fecha o dropdown
  };

  const toggleColumnSelector = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setModalPosition({
        top: buttonRect.bottom + window.scrollY,
        left: buttonRect.left + window.scrollX,
      });
    }
    setIsOpenMenuCollumn((prev) => !prev);
  };

  useEffect(() => {
    // Função para fechar o menu se o clique for fora dele
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target as Node)
      ) {
        setIsOpenMenuCollumn(false); // Fecha o dropdown
      }
    };

    // Adiciona o event listener quando o menu estiver aberto
    if (isOpenMenuCollumn) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Limpeza do event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpenMenuCollumn]);

  useEffect(() => {
    const fetchDependentes = async () => {
      try {
        // Carrega todos os dependentes inicialmente
        const data = await dependenteService.getAllDependentesByContribuinte(
          contribuinteData.id
        );
        setDependentes(data); // Armazena todos os dependentes

        if (searchValue) {
          // Se há um valor de pesquisa, filtra os dependentes
          const filtered = await dependenteService.searchDependente(
            { cpfCin: searchValue, nome: searchValue },
            contribuinteData.id
          );

          // Verifica se há resultados após a busca
          if (filtered.dependente && filtered.dependente.length > 0) {
            setFilteredDependentes(filtered.dependente); // Atualiza apenas os dependentes filtrados
          } else {
            setFilteredDependentes([]); // Caso não haja resultados, lista vazia
          }
        } else {
          // Se não há pesquisa, define filteredDependentes para todos os dependentes
          setFilteredDependentes(data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Erro ao buscar dependentes:", error);
        setError("Não foi possível carregar os dependentes.");
        setLoading(false);
        setFilteredDependentes([]); // Se erro, lista vazia
      }
    };

    fetchDependentes();
  }, [contribuinteData.id, searchValue]); // A dependência de `searchValue` é importante aqui
  const menuRefsMap = useRef<Map<number, HTMLDivElement | null>>(new Map());
  return (
    <MainContainer>
      <div className="page contribuinte">
        <div className="body taxpayer">
          {loading ? (
            <LoadingOverlay>
              <LoadingGif src={loadSCGif} alt="Carregando..." />
            </LoadingOverlay>
          ) : (
            <>
              <div className="listar-dependentes-title">
                <h2>Dependentes</h2>
                <div className="divider-listar-dependentes"></div>
              </div>
              {/* Label para o campo */}
              <Label htmlFor="dependente-cpf-search" id="pesquisa-cpf">
                Pesquisar por Nome ou CPF:
              </Label>
              <ContainerFilter>
                <InputComponent
                  type="text"
                  id="dependente-cpf-search"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Busque por Nome ou CPF do contribuinte"
                  maxLength={50}
                  autoFocus
                  color="#000"
                  width={400}
                  border="1px solid #ccc"
                  iconLeft={
                    <IoSearchSharp size={24} color={Colors.inputIcon} />
                  }
                />

                <ContainerButtons>
                  <Button
                    schemaButton="out-primary"
                    action={() => setIsOpenMenuCollumn(!isOpenMenuCollumn)}
                    // action={() => setShowColumnSelector(!showColumnSelector)}
                    icon={
                      <MdOutlineViewColumn
                        size={20}
                        style={{ marginRight: "8px" }}
                      />
                    }
                  >
                    Colunas
                  </Button>

                  <Dropdown.Menu
                    ref={dropdownMenuRef}
                    show={isOpenMenuCollumn}
                    style={{ marginTop: "50px" }}
                  >
                    <DropdownScroll>
                      {[
                        "CPF",
                        "Nome",
                        "Sexo",
                        "Data de Nascimento",
                        "Email",
                        "Celular",
                        "Telefone Residencial",
                        "Telefone Comercial",
                        "Telefone Recado",
                      ].map((column, index, array) => (
                        <React.Fragment key={column}>
                          {!defaultColumns.includes(column) && (
                            <Dropdown.Item
                              eventKey="1"
                              onClick={() => handleColumnToggle(column)}
                              style={{ color: Colors.primary }}
                            >
                              <img
                                src={
                                  tempVisibleColumns.includes(column)
                                    ? "/images/checkbox-marked.svg"
                                    : "/images/checkbox-blank-outline.svg"
                                }
                                alt={
                                  tempVisibleColumns.includes(column)
                                    ? defaultColumns.includes(column)
                                      ? "Bloqueado"
                                      : "Selecionado"
                                    : "Não selecionado"
                                }
                                className="checkbox-image"
                              />
                              <label>{column}</label>
                              {array
                                .filter((col) => !defaultColumns.includes(col))
                                .indexOf(column) <
                                array.filter(
                                  (col) => !defaultColumns.includes(col)
                                ).length -
                                  1 && <DropdownDivider />}
                            </Dropdown.Item>
                          )}
                        </React.Fragment>
                      ))}
                    </DropdownScroll>
                    {JSON.stringify(tempVisibleColumns.sort()) !==
                      JSON.stringify(dependetesVisibleColumns.sort()) && (
                      <Dropdown.Item>
                        <MenuTableDivider />
                        <Button
                          schemaButton="with-margin"
                          action={handleApplyColumns} // Aplica as mudanças ao clicar
                        >
                          Aplicar
                        </Button>
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                  {editOnly && (
                    <Button
                      schemaButton="full-primary"
                      action={handleAddDependente}
                      icon={
                        <IoIosAddCircleOutline
                          size={20}
                          style={{ marginRight: "8px" }}
                        />
                      }
                    >
                      Cadastrar dependente
                    </Button>
                  )}
                  {/* <Button
                    schemaButton="full-secondary"
                    action={() => navigate(-1)}
                    icon={
                      <IoIosArrowBack
                        size={20}
                        style={{ marginRight: "8px" }}
                      />
                    }
                  >
                    Voltar
                  </Button> */}
                </ContainerButtons>
              </ContainerFilter>
              {error ? (
                <div
                  id="erro-mensagem"
                  className="alert alert-danger"
                  role="alert"
                >
                  {error}
                </div>
              ) : (
                <TableContainer>
                  <Table id="dependentes-tabela" style={{ minHeight: "175px" }}>
                    <TableHead>
                      <TableRow>
                        {dependetesVisibleColumns.includes("CPF") && (
                          <TableHeader>CPF</TableHeader>
                        )}
                        {dependetesVisibleColumns.includes("Nome") && (
                          <TableHeader>Nome</TableHeader>
                        )}
                        {dependetesVisibleColumns.includes("Sexo") && (
                          <TableHeader>Sexo</TableHeader>
                        )}
                        {dependetesVisibleColumns.includes(
                          "Data de Nascimento"
                        ) && <TableHeader>Data Nascimento</TableHeader>}
                        {dependetesVisibleColumns.includes("Email") && (
                          <TableHeader>Email</TableHeader>
                        )}
                        {dependetesVisibleColumns.includes("Celular") && (
                          <TableHeader>Celular</TableHeader>
                        )}
                        {dependetesVisibleColumns.includes(
                          "Telefone Residencial"
                        ) && <TableHeader>Telefone Residencial</TableHeader>}
                        {dependetesVisibleColumns.includes(
                          "Telefone Comercial"
                        ) && <TableHeader>Telefone Comercial</TableHeader>}
                        {dependetesVisibleColumns.includes(
                          "Telefone Recado"
                        ) && <TableHeader>Telefone Recado</TableHeader>}
                        {editOnly && <TableHeader>Ações</TableHeader>}
                      </TableRow>
                    </TableHead>
                    <tbody>
                      {filteredDependentes.length > 0 ? (
                        filteredDependentes.map((dependente) => (
                          <TableRow key={dependente.id}>
                            {dependetesVisibleColumns.includes("CPF") && (
                              <TableData>
                                {formatCpf(dependente.cpfCin ?? "")}
                              </TableData>
                            )}
                            {dependetesVisibleColumns.includes("Nome") && (
                              <TableData>{dependente.nome}</TableData>
                            )}
                            {dependetesVisibleColumns.includes("Sexo") && (
                              <TableData>
                                {(() => {
                                  switch (dependente.sexo) {
                                    case 1:
                                      return "Masculino";
                                    case 2:
                                      return "Feminino";
                                    case 3:
                                      return "Outro";
                                    case 4:
                                      return "Não Declarado";
                                    default:
                                      return "Não Informado";
                                  }
                                })()}
                              </TableData>
                            )}
                            {dependetesVisibleColumns.includes(
                              "Data de Nascimento"
                            ) && (
                              <TableData>
                                {dependente.dataNascimento
                                  ? new Date(
                                      dependente.dataNascimento
                                    ).toLocaleDateString("pt-BR", {
                                      timeZone: "UTC",
                                    })
                                  : "-- / -- / ----"}
                              </TableData>
                            )}
                            {dependetesVisibleColumns.includes("Email") && (
                              <TableData>{dependente.email}</TableData>
                            )}
                            {dependetesVisibleColumns.includes("Celular") && (
                              <TableData>{dependente.celular}</TableData>
                            )}
                            {dependetesVisibleColumns.includes(
                              "Telefone Residencial"
                            ) && (
                              <TableData>
                                {dependente.telefoneResidencial}
                              </TableData>
                            )}
                            {dependetesVisibleColumns.includes(
                              "Telefone Comercial"
                            ) && (
                              <TableData>
                                {dependente.telefoneComercial}
                              </TableData>
                            )}
                            {dependetesVisibleColumns.includes(
                              "Telefone Recado"
                            ) && (
                              <TableData>{dependente.telefoneRecado}</TableData>
                            )}
                            {editOnly && (
                              <TableData
                                style={{
                                  position: "relative",
                                  overflow: "visible",
                                }}
                              >
                                <ButtonAction
                                  onClick={() => toggleDropdown(dependente.id)}
                                  style={{ marginLeft: "auto" }}
                                >
                                  {isOpen === dependente.id ? (
                                    <IoMdClose
                                      cursor={"pointer"}
                                      size={20}
                                      style={{ color: Colors.primary }}
                                    />
                                  ) : (
                                    <SlOptionsVertical
                                      cursor={"pointer"}
                                      size={20}
                                      style={{ color: Colors.primary }}
                                    />
                                  )}
                                </ButtonAction>
                                <MenuTable
                                  show={isOpen === dependente.id}
                                  align="start"
                                  onHide={() => setIsOpen(null)}
                                  ref={(el: HTMLDivElement | null) => {
                                    // Armazena a referência no mapa usando o ID do registro como chave
                                    menuRefsMap.current.set(dependente.id, el);
                                  }}
                                  isLastRows={
                                    filteredDependentes.length > 0 &&
                                    filteredDependentes.indexOf(dependente) >=
                                      filteredDependentes.length -
                                        Math.ceil(
                                          filteredDependentes.length / 2
                                        )
                                  }
                                >
                                  <MenuTableItem
                                    onClick={() => {
                                      handleEditDependente(dependente);
                                      setIsOpen(null);
                                    }}
                                  >
                                    Editar
                                  </MenuTableItem>
                                  <MenuTableDivider />
                                  <MenuTableItem
                                    onClick={() => {
                                      handleDeleteDependente(dependente.id);
                                      setIsOpen(null);
                                    }}
                                  >
                                    Excluir
                                  </MenuTableItem>
                                </MenuTable>
                              </TableData>
                            )}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableData
                            colSpan={dependetesVisibleColumns.length + 1}
                            className="text-center text-muted"
                          >
                            Nenhum dependente cadastrado.
                          </TableData>
                        </TableRow>
                      )}
                    </tbody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Modal para criar/editar dependente */}
          {showModal && (
            <DependenteModal
              show={showModal && editOnly} // Controle da visibilidade do modal
              onHide={() => setShowModal(false)} // Fechar o modal corretamente
              dependenteData={selectedDependente}
              idContribuinte={contribuinteData.id}
              onSuccess={handleModalSuccess}
              setNewDependente={setNewDependenteData}
            />
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default DependentesList;
